@if (selectedIncident$ | async; as inc) {
  <div class="gtd-modal-backdrop" (click)="close()">
    <div class="gtd-modal-card" (click)="$event.stopPropagation()">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <div class="attack-icon-badge" [style.border-color]="inc.color">
            <img [src]="inc.iconUrl" [alt]="inc.attacktype" class="modal-tactical-img" />
          </div>
          <div class="header-meta">
            <div class="meta-sub">
              <span class="attack-badge" [style.border-color]="inc.color">
                <img [src]="inc.iconUrl" [alt]="inc.attacktype" class="badge-mini-icon" />
                {{ inc.attacktype }}
              </span>
              <span class="location-badge" (click)="flyToState(inc.provstate)" title="Click to view state">
                <i class="fa-solid fa-location-dot"></i> {{ inc.city }}, {{ inc.provstate }}
              </span>
              <span class="date-badge">
                <i class="fa-regular fa-calendar"></i> {{ inc.dateStr }}
              </span>
            </div>
            <h3 class="incident-title">TD Event #{{ inc.eventid }}</h3>
          </div>
        </div>

        <div class="header-right">
          <button class="action-btn fly-btn" (click)="flyToIncident(inc)" title="Fly Camera to Incident Coordinates">
            <i class="fa-solid fa-crosshairs"></i>
          </button>
          <button class="action-btn close-btn" (click)="close()" title="Close Dossier">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="modal-body custom-scroll">
        <!-- Casualties & Impact Grid -->
        <div class="section-title">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>Casualties & Direct Impact</span>
        </div>

        <div class="stats-grid">
          <div class="stat-card" [class.highlight-red]="inc.nkill > 0">
            <span class="stat-icon-wrap icon-red">
              <i class="fa-solid fa-skull"></i>
            </span>
            <div class="stat-info">
              <span class="stat-num">{{ inc.nkill }}</span>
              <span class="stat-lbl">Fatalities (Killed)</span>
            </div>
          </div>

          <div class="stat-card" [class.highlight-orange]="inc.nwound > 0">
            <span class="stat-icon-wrap icon-orange">
              <i class="fa-solid fa-user-injured"></i>
            </span>
            <div class="stat-info">
              <span class="stat-num">{{ inc.nwound }}</span>
              <span class="stat-lbl">Wounded / Injured</span>
            </div>
          </div>

          <div class="stat-card" [class.highlight-purple]="inc.ishostkid">
            <span class="stat-icon-wrap icon-purple">
              <i class="fa-solid fa-handcuffs"></i>
            </span>
            <div class="stat-info">
              <span class="stat-num">{{ inc.nhostkid > 0 ? inc.nhostkid : (inc.ishostkid ? 'Yes' : '0') }}</span>
              <span class="stat-lbl">Hostages Kidnapped</span>
            </div>
          </div>

          <div class="stat-card">
            <span class="stat-icon-wrap icon-cyan">
              <i class="fa-solid fa-bomb"></i>
            </span>
            <div class="stat-info">
              <span class="stat-num">{{ inc.suicide ? 'Yes' : 'No' }}</span>
              <span class="stat-lbl">Suicide Attack</span>
            </div>
          </div>
        </div>

        <!-- Threat Actor / Perpetrator Details -->
        <div class="section-title">
          <i class="fa-solid fa-mask"></i>
          <span>Threat Actor / Perpetrator</span>
        </div>

        <div class="actor-card">
          <div class="actor-row">
            <span class="actor-lbl">Perpetrator Group:</span>
            <span class="actor-val highlight-group">{{ inc.gname }}</span>
          </div>
          @if (inc.gsubname) {
            <div class="actor-row">
              <span class="actor-lbl">Sub-faction:</span>
              <span class="actor-val">{{ inc.gsubname }}</span>
            </div>
          }
          <div class="actor-row">
            <span class="actor-lbl">Claimed Responsibility:</span>
            <span class="actor-val">
              {{ inc.claimed ? 'Yes (' + (inc.claimmode || 'Direct Announcement') + ')' : 'No / Unconfirmed' }}
            </span>
          </div>
          @if (inc.motive) {
            <div class="actor-motive-wrap">
              <span class="actor-lbl">Stated Motive:</span>
              <p class="actor-motive-text">{{ inc.motive }}</p>
            </div>
          }
        </div>

        <!-- Tactical Profile & Targets -->
        <div class="section-title">
          <i class="fa-solid fa-crosshairs"></i>
          <span>Tactical Profile & Target Info</span>
        </div>

        <div class="specs-grid">
          <div class="spec-card">
            <span class="spec-label">Target Category</span>
            <span class="spec-val">{{ inc.targtype }}</span>
          </div>
          <div class="spec-card">
            <span class="spec-label">Specific Target</span>
            <span class="spec-val">{{ inc.target || 'Not Specified' }}</span>
          </div>
          <div class="spec-card">
            <span class="spec-label">Primary Weapon</span>
            <span class="spec-val tag-weapon">{{ inc.weaptype }}</span>
          </div>
          <div class="spec-card">
            <span class="spec-label">Weapon Details</span>
            <span class="spec-val">{{ inc.weapdetail || inc.weapsubtype || 'Standard firearms / explosives' }}</span>
          </div>
          @if (inc.corp) {
            <div class="spec-card full-span">
              <span class="spec-label">Target Entity / Corporation</span>
              <span class="spec-val">{{ inc.corp }}</span>
            </div>
          }
          <div class="spec-card full-span">
            <span class="spec-label">Coordinates (Latitude, Longitude)</span>
            <span class="spec-val font-mono text-cyan">
              {{ inc.latitude | number:'1.6-6' }}, {{ inc.longitude | number:'1.6-6' }}
              @if (inc.location) {
                <span class="location-detail">({{ inc.location }})</span>
              }
            </span>
          </div>
        </div>

        <!-- Full Summary Narrative -->
        @if (inc.summary) {
          <div class="section-title">
            <i class="fa-solid fa-file-lines"></i>
            <span>Incident Narrative & Chronology</span>
          </div>
          <div class="summary-box">
            <p class="summary-text">{{ inc.summary }}</p>
          </div>
        }

        <!-- Sources / Citations -->
        @if (inc.scite1 || inc.scite2 || inc.dbsource) {
          <div class="section-title">
            <i class="fa-solid fa-book-bookmark"></i>
            <span>Intelligence Sources & Verification</span>
          </div>
          <div class="sources-box">
            @if (inc.scite1) {
              <div class="source-item">
                <i class="fa-solid fa-quote-left source-icon"></i>
                <span>{{ inc.scite1 }}</span>
              </div>
            }
            @if (inc.scite2) {
              <div class="source-item">
                <i class="fa-solid fa-quote-left source-icon"></i>
                <span>{{ inc.scite2 }}</span>
              </div>
            }
            @if (inc.dbsource) {
              <div class="source-db-tag">
                <span>Database Source:</span> <b>{{ inc.dbsource }}</b>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
}
